import { getCookie, setCookie } from "src/utils/cookieUtils"
import { logger } from "src/utils/logger"

type TSessionId = number

const COOKIE_KEY = "minut.session_id"

const SESSION_EXPIRATION_TIME = 1000 * 60 * 30

/**
 * Gets the current session ID from cookies.
 *
 * @returns The session ID if found and valid, null otherwise
 */
function getSessionId(): TSessionId | null {
  const sessionId = getCookie(COOKIE_KEY)
  if (!sessionId) {
    return null
  }

  const parsedSessionId = parseInt(sessionId)

  if (isNaN(parsedSessionId)) {
    return null
  }

  return parsedSessionId
}

/**
 * Updates the expiration time of an existing session cookie.
 * This function:
 * 1. Gets the current session ID
 * 2. If session ID exists, extends its expiration time by SESSION_EXPIRATION_TIME
 * 3. If no session ID exists, does nothing
 */
function touchSession() {
  const sessionId = getSessionId()
  if (!sessionId) {
    return
  }

  setCookie(COOKIE_KEY, sessionId.toString(), {
    path: "/",
    expires: new Date(Date.now() + SESSION_EXPIRATION_TIME),
  })
}

/**
 * Creates a new session ID and stores it in a cookie.
 * This function:
 * 1. Generates a new session ID using the current timestamp
 * 2. Stores the session ID in a cookie with the key COOKIE_KEY
 *
 * @returns The newly created session ID
 */
function createSession(): TSessionId {
  const sessionId = Date.now()

  setCookie(COOKIE_KEY, JSON.stringify(sessionId), { path: "/" })

  return sessionId
}

/**
 * Retrieves the current session ID.
 * This function:
 * 1. Gets the session ID from the cookie
 * 2. If no session ID is found, creates a new one
 * 3. Touches the session to update its expiration time
 *
 * @returns The current session ID
 */
function retrieveSessionId() {
  const sessionId = getSessionId()

  if (!sessionId) {
    logger.info("No session id found, creating new session")
    return createSession()
  }

  logger.info("Session id found, touching session")
  touchSession()

  return sessionId
}

/**
 * SessionService is a the service that handles the session for the web app.
 *
 * A session is a time-limited identifier for a user's interaction with the web app.
 * It is used to track user activity and behavior across different pages and devices.
 *
 * The session is stored in a cookie with the key COOKIE_KEY.
 *
 * The session is considered active if the cookie is present.
 * The session ends when the cookie expires, which is 30 minutes after the last activity.
 *
 */
export const SessionService = {
  retrieve: retrieveSessionId,
}
